import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Target,
  Sparkles,
  BookOpen,
  Award,
  BarChart3,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from "lucide-react";

const SkillGapAnalysis = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecs, setLoadingRecs] = useState(false);

  useEffect(() => {
    fetchSkillGapAnalysis();
  }, []);

  const fetchSkillGapAnalysis = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/skills/gap-analysis`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to load analysis");
      }
    } catch (error) {
      toast.error("Failed to load skill gap analysis");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const response = await fetch(`${API}/skills/recommendations`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      } else {
        toast.error("Failed to get recommendations");
      }
    } catch (error) {
      toast.error("Failed to get recommendations");
    } finally {
      setLoadingRecs(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                SKILL <span className="text-crimson">GAP ANALYSIS</span>
              </h1>
              <p className="text-gray-400">Identify skills to improve for your target roles</p>
            </div>
            <Button
              onClick={fetchSkillGapAnalysis}
              variant="outline"
              className="border-[#333] text-white hover:border-crimson"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

        {analysis && (
          <>
            {/* Market Readiness Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-obsidian-light border border-[#222] p-8 mb-8 text-center"
            >
              <h2 className="font-heading text-lg text-gray-400 mb-4">MARKET READINESS SCORE</h2>
              <div className="relative w-48 h-48 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    fill="none"
                    stroke="#222"
                    strokeWidth="12"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    fill="none"
                    stroke="#FF2A2A"
                    strokeWidth="12"
                    strokeDasharray={`${(analysis.market_readiness_score / 100) * 553} 553`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-heading text-5xl font-bold text-white">
                    {analysis.market_readiness_score}%
                  </span>
                </div>
              </div>
              <p className="text-gray-400">
                Based on {analysis.jobs_analyzed} jobs in {analysis.target_industries?.join(", ") || "all industries"}
              </p>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-obsidian-light border border-[#222] p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-400 uppercase text-sm">Your Skills</span>
                </div>
                <p className="font-heading text-3xl font-bold text-white">
                  {analysis.user_skills?.length || 0}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-obsidian-light border border-[#222] p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-crimson" />
                  <span className="text-gray-400 uppercase text-sm">In-Demand Skills You Have</span>
                </div>
                <p className="font-heading text-3xl font-bold text-white">
                  {analysis.skill_strengths?.length || 0}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-obsidian-light border border-[#222] p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <span className="text-gray-400 uppercase text-sm">Skill Gaps</span>
                </div>
                <p className="font-heading text-3xl font-bold text-white">
                  {analysis.skill_gaps?.length || 0}
                </p>
              </motion.div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Skill Strengths */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-obsidian-light border border-[#222] p-6"
              >
                <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  YOUR STRENGTHS
                </h3>
                <div className="space-y-3">
                  {analysis.skill_strengths?.slice(0, 8).map((skill, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-white capitalize">{skill.skill}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-[#222]">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${skill.demand_percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-400 w-12">{skill.demand_percentage}%</span>
                      </div>
                    </div>
                  ))}
                  {(!analysis.skill_strengths || analysis.skill_strengths.length === 0) && (
                    <p className="text-gray-500">Add skills to your profile to see strengths</p>
                  )}
                </div>
              </motion.div>

              {/* Skill Gaps */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-obsidian-light border border-[#222] p-6"
              >
                <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-crimson" />
                  SKILLS TO DEVELOP
                </h3>
                <div className="space-y-3">
                  {analysis.skill_gaps?.slice(0, 8).map((skill, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white capitalize">{skill.skill}</span>
                        <span className="text-sm text-crimson">{skill.demand_count} jobs</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-full h-2 bg-[#222]">
                          <div 
                            className="h-full bg-crimson"
                            style={{ width: `${skill.demand_percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-400 w-12">{skill.demand_percentage}%</span>
                      </div>
                      {skill.sample_jobs && skill.sample_jobs.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Found in: {skill.sample_jobs.slice(0, 2).join(", ")}
                        </p>
                      )}
                    </div>
                  ))}
                  {(!analysis.skill_gaps || analysis.skill_gaps.length === 0) && (
                    <p className="text-gray-500 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-500" />
                      Great job! You have all the in-demand skills.
                    </p>
                  )}
                </div>
              </motion.div>
            </div>

            {/* AI Recommendations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-obsidian-light border border-crimson/30 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-crimson" />
                  AI LEARNING RECOMMENDATIONS
                </h3>
                {!recommendations && (
                  <Button
                    onClick={fetchRecommendations}
                    disabled={loadingRecs}
                    className="bg-crimson hover:bg-crimson-hover text-white"
                    data-testid="get-recommendations-btn"
                  >
                    {loadingRecs ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <BookOpen className="w-4 h-4 mr-2" /> Get Recommendations
                      </>
                    )}
                  </Button>
                )}
              </div>

              {recommendations ? (
                <pre className="whitespace-pre-wrap font-body text-sm text-gray-300 leading-relaxed">
                  {recommendations.ai_recommendations}
                </pre>
              ) : (
                <p className="text-gray-400">
                  Click the button above to get personalized AI-powered learning recommendations based on your skill gaps and career goals.
                </p>
              )}
            </motion.div>
          </>
        )}

        {!analysis && !loading && (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Complete your profile to see skill gap analysis</p>
            <Button
              onClick={() => navigate("/profile/build")}
              className="bg-crimson hover:bg-crimson-hover text-white"
            >
              Build Profile
            </Button>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default SkillGapAnalysis;
